import {Component, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {CampaignsApiService} from '@core/services/api/campaigns-api.service';
import {AlertService} from '@core/services/alert.service';
import {Step1BasicInfoComponent} from './steps/step-1-basic-info.component';
import {Step2PreviewComponent} from './steps/step-2-preview.component';

@Component({
    selector: 'app-campaign-creation-dialog',
    standalone: true,
    imports: [
        CommonModule,
        Step1BasicInfoComponent,
        Step2PreviewComponent
    ],
    templateUrl: './campaign-creation-dialog.component.html',
    styleUrl: './campaign-creation-dialog.component.scss'
})
export class CampaignCreationDialogComponent {
    protected activeModal = inject(NgbActiveModal);
    private campaignsApiService = inject(CampaignsApiService);
    private alertService = inject(AlertService);

    // Inputs do componente pai
    filters: any = {};
    totalCompanies = 0;

    // Estado do wizard
    currentStep = signal(1);
    isCreating = signal(false);

    // Dados da campanha
    campaignData = signal({
        name: '',
        description: '',
        maxCompanies: undefined as number | undefined
    });

    // Dados do preview
    previewData = signal({
        totalCompanies: 0,
        totalEstimatedValue: 0,
        companies: []
    });

    // Validação da etapa 1
    isStep1Valid = signal(false);
    // Validação da etapa 2
    isStep2Valid = signal(true); // Começa como true pois maxCompanies é opcional

    close(): void {
        this.activeModal.dismiss();
    }

    nextStep(): void {
        // Validar etapa 1 antes de avançar
        if (this.currentStep() === 1 && !this.isStep1Valid()) {
            return;
        }

        if (this.currentStep() < 2) {
            this.currentStep.set(this.currentStep() + 1);
        }
    }

    updateStep1Validity(isValid: boolean): void {
        this.isStep1Valid.set(isValid);
    }

    updateStep2Validity(isValid: boolean): void {
        this.isStep2Valid.set(isValid);
    }

    previousStep(): void {
        if (this.currentStep() > 1) {
            this.currentStep.set(this.currentStep() - 1);
        }
    }

    goToStep(step: number): void {
        if (this.canGoToStep(step)) {
            this.currentStep.set(step);
        }
    }

    canGoToStep(step: number): boolean {
        // Step 1 sempre acessível
        if (step === 1) return true;
        // Step 2 requer step 1 válido
        if (step === 2) return this.isStep1Valid();
        return false;
    }

    updateCampaignData(data: any): void {
        this.campaignData.update(current => ({...current, ...data}));
    }

    updatePreviewData(data: any): void {
        this.previewData.set(data);
    }

    createCampaign(): void {
        // Validar step 2 antes de criar
        if (!this.isStep2Valid()) {
            this.alertService.error('Erro de Validação', 'Por favor, corrija os erros antes de criar a campanha.');
            return;
        }

        this.isCreating.set(true);

        const payload = {
            ...this.campaignData(),
            filters: this.filters
        };

        // Remove maxCompanies se for undefined
        if (payload.maxCompanies === undefined) {
            delete payload.maxCompanies;
        }

        this.campaignsApiService.create(payload).subscribe({
            next: (response: any) => {
                if (response.success) {
                    this.alertService.success('Campanha Criada!',`A campanha "${payload.name}" foi criada com sucesso.`);
                    this.activeModal.close(response.data);
                } else {
                    this.alertService.error('Erro', 'Falha ao criar campanha');
                    this.isCreating.set(false);
                }
            },
            error: (_error) => {
                this.isCreating.set(false);
                if (_error.error.code === 'CAMPAIGN_MAX_COMPANIES_EXCEEDED'){
                    this.alertService.error("Ops", `A campanha não pode ser criada porque o número de empresas (${_error.error.data.found}) excede o limite máximo permitido (${_error.error.data.maximum}). Por favor, ajuste os filtros da campanha para reduzir o número de empresas e tente novamente.`);
                    return;
                }
                if (_error.error.code === 'NO_COMPANIES_AVAILABLE'){
                    this.alertService.error("Ops", `Não há empresas disponíveis que correspondam aos filtros selecionados. Por favor, ajuste os filtros da campanha e tente novamente.`);
                    return;
                }

                this.alertService.operationFailure();
            }
        });
    }
}
